import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { formatCurrency } from "../../../../services/api";
import noImagePlaceholder from "../../../../assets/img/not-image.png";
import ComingSoonBadge from "../../../../components/ui/ComingSoonBadge";
import styles from "./LotteryLogoStrip.module.css";
import {
  isLotteryBettingClosed,
  getOpenClosingBadgeText,
  isLotteryComingSoon,
} from "../lotteryHelpers";

const getLotteryImage = (lottery) => {
  const src = lottery.image || lottery.logo || lottery.img || lottery.imagen;
  return src?.trim() ? src : noImagePlaceholder;
};

const getJackpotLabel = (lottery) => {
  const amount = lottery.amount ?? lottery.prize_amount ?? lottery.premio;
  if (amount == null || amount === "") return null;
  return formatCurrency(amount);
};

/**
 * Paso 1 en CELULAR: fila deslizable de tarjetas con el LOGO de cada lotería (en vez del
 * desplegable de solo texto). Mismas reglas que las tarjetas de escritorio: cerradas
 * deshabilitadas y "Próximamente" en gris al final, sin poder seleccionarse.
 *
 * @param {React.Ref<HTMLDivElement>} [props.stripRef] Para llevar la vista aquí desde "Cambiar".
 */
export default function LotteryLogoStrip({
  lotteries = [],
  comingSoon = [],
  selectedId = null,
  onSelect,
  now,
  stripRef,
}) {
  const [broken, setBroken] = useState({});
  const scrollerRef = useRef(null);

  const items = useMemo(
    () => [
      ...(lotteries || [])
        .filter((l) => l?.id != null && String(l?.name || "").trim() && !isLotteryComingSoon(l))
        .map((l) => ({ ...l, _soon: false })),
      ...(comingSoon || [])
        .filter((l) => l?.id != null && String(l?.name || "").trim())
        .map((l) => ({ ...l, _soon: true })),
    ],
    [lotteries, comingSoon]
  );

  // Al elegir (o al llegar con una lotería ya elegida) se centra su tarjeta en la fila.
  useEffect(() => {
    if (selectedId == null || !scrollerRef.current) return;
    const card = scrollerRef.current.querySelector(`[data-lottery-id="${String(selectedId)}"]`);
    if (card && typeof card.scrollIntoView === "function") {
      card.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [selectedId]);

  const logo = (lottery) => (
    <img
      src={broken[lottery.id] ? noImagePlaceholder : getLotteryImage(lottery)}
      alt=""
      className={styles.logo}
      loading="lazy"
      onError={() => setBroken((prev) => ({ ...prev, [lottery.id]: true }))}
    />
  );

  return (
    <div className={styles.wrap} ref={stripRef}>
      <p className={styles.label} id="lottery-strip-label">
        Paso 1 · Elige tu lotería
      </p>
      <div
        className={styles.scroller}
        ref={scrollerRef}
        role="list"
        aria-labelledby="lottery-strip-label"
      >
        {items.map((lottery) => {
          const jackpot = getJackpotLabel(lottery);
          if (lottery._soon) {
            return (
              <div
                key={`soon-${lottery.id}`}
                role="listitem"
                className={`${styles.card} ${styles.cardSoon}`}
                aria-disabled="true"
                title={`${lottery.name} · Próximamente`}
              >
                <ComingSoonBadge size="sm" className={styles.soonBadge} />
                <div className={styles.logoWrap}>{logo(lottery)}</div>
                <span className={styles.name}>{lottery.name}</span>
                {jackpot && <span className={styles.prize}>{jackpot}</span>}
              </div>
            );
          }
          const selected = String(lottery.id) === String(selectedId);
          const closed = isLotteryBettingClosed(lottery, now);
          const openBadge = getOpenClosingBadgeText(lottery, now);
          return (
            <div key={lottery.id} role="listitem" className={styles.item}>
              <button
                type="button"
                data-lottery-id={String(lottery.id)}
                className={`${styles.card} ${selected ? styles.cardSelected : ""} ${closed ? styles.cardClosed : ""}`}
                onClick={() => {
                  if (!closed) onSelect?.(lottery);
                }}
                disabled={closed}
                aria-pressed={selected}
                title={closed ? `${lottery.name} · Cerrada` : lottery.name}
              >
                {selected && (
                  <span className={styles.check} aria-hidden>
                    <FontAwesomeIcon icon={faCheck} />
                  </span>
                )}
                {closed && <span className={styles.closed}>CERRADO</span>}
                <div className={styles.logoWrap}>{logo(lottery)}</div>
                <span className={styles.name}>{lottery.name}</span>
                {jackpot && <span className={styles.prize}>{jackpot}</span>}
                {!closed && openBadge ? <span className={styles.open}>{openBadge}</span> : null}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
